/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be

import io.peekandpoke.klang.audio_be.ignitor.IgnitorRegistry
import io.peekandpoke.klang.audio_be.ignitor.registerDefaults
import io.peekandpoke.klang.audio_be.katalyst.KatalystRegistry
import io.peekandpoke.klang.audio_be.master.MasterBus
import io.peekandpoke.klang.audio_be.warehouse.ResourceWarehouse
import io.peekandpoke.klang.audio_bridge.infra.KlangCommLink

/**
 * The shared backend state — everything that is **one per backend** and injected into every
 * [PlaybackEngine] / `VoiceScheduler`, instead of threading half a dozen separate parameters.
 *
 * Holds the shared services (sample cache, registries, IPC link, config) and the read-only
 * [clock], and the [warehouse] every engine rents from — cylinders included: an engine's
 * `Cylinders` map is its own, but the cylinders in it come from the warehouse's shelf and go back
 * there when the engine is disposed. Per-engine state (that map, the scheduling timeline) is NOT
 * here. See KLANG-00176 (per playback engine).
 *
 * Note: `performanceTimeMs` is transitional — once diagnostics emission moves up to the dispatcher
 * (D5), the per-scheduler wall-clock read leaves this context.
 */
class AudioBackendContext(
    val sampleRate: Int,
    val blockFrames: Int,
    val commLink: KlangCommLink.BackendEndpoint,
    /** Parent ignitor registry — each engine's scheduler forks it per playback. */
    val ignitorRegistry: IgnitorRegistry,
    /**
     * Parent Katalyst registry; each engine forks it per playback. The one chain registry for both
     * positions: the engine's cylinders and its [MasterBus] resolve names against the same fork.
     */
    val katalystRegistry: KatalystRegistry,
    /** The single audio timeline (read-only here; written by the main loop via [BackendClock]). */
    val clock: RenderClock,
    /** Wall clock in ms, for the render headroom. The diagnostics report the timeline, never this (KLANG-00399). */
    val performanceTimeMs: () -> Double,
    /**
     * Fixed seed for the per-playback unison phase pools (`ignitor.PhasePools`); null = live —
     * each playback gets a fresh stream ("takes vary"). The offline renderer passes a fixed Int
     * so pool vocabularies reproduce.
     */
    val phasePoolSeed: Int? = null,
    /** What became of each voice at its start, for the [OfflineBackend]'s specs; null (the live backends) for nobody. */
    val voiceWatch: VoiceWatch? = null,
    /**
     * Where the backend's expensive buffers come from — one per backend, shared across playbacks and
     * kept from the warmup engine rather than disposed with it. Owned here, not a Kotlin `object`,
     * so specs and the offline renderer get their own. See KLANG-00230 (resource warehouse).
     */
    val warehouse: ResourceWarehouse = ResourceWarehouse(
        sampleRate = sampleRate,
        blockFrames = blockFrames,
        samples = SampleStore(commLink),
    ),
) {
    val sampleRateDouble: Double = sampleRate.toDouble()

    /** The backend's sample PCM store — owned by the [warehouse] since the stats feed; same object, same behaviour. */
    val sampleStore: SampleStore get() = warehouse.samples

    companion object {
        /**
         * The canonical DSP block size. **Every host must render in blocks of this size.**
         *
         * This is NOT a latency/throughput knob — it is a *tone* parameter, because several parts
         * of the engine update once per block and therefore derive their rate from it:
         *
         *  - `analogDriftStepRate` = `sampleRate / blockFrames`: the filter analog-drift time
         *    constants (`AnalogDriftCoeffs`) are derived from it, so a different block size gives
         *    audibly different drift.
         *  - the SVF cutoff envelope (`SvfIgnitor`): per-block recompute granularity.
         *  - `VoiceScheduler`'s startup epoch and admission both resolve to a BLOCK boundary (a
         *    playback's zero point is the next block to be rendered; anything starting before the
         *    block being promoted for is dropped, block-framing B1/B2).
         *  - `MasterBus` chain crossfades — start rounds to the current block.
         *
         * 128 because that is the Web Audio API render quantum: the browser worklet gets its block
         * size handed to it by Chrome and cannot choose (`KlangAudioWorklet.process`). The browser
         * is therefore the blueprint, and the JVM backend + offline renderer follow it so a WAV
         * render sounds like what you hear live.
         */
        const val RENDER_QUANTUM_FRAMES: Int = 128

        /**
         * Builds a context with the standard shared services (sample cache + seeded registries).
         * The caller creates the mutable [BackendClock], passes it in (as a read-only [RenderClock]),
         * and keeps its own reference to advance it.
         */
        fun create(
            sampleRate: Int,
            blockFrames: Int,
            commLink: KlangCommLink.BackendEndpoint,
            clock: RenderClock,
            performanceTimeMs: () -> Double = { 0.0 },
            phasePoolSeed: Int? = null,
            voiceWatch: VoiceWatch? = null,
        ): AudioBackendContext = AudioBackendContext(
            sampleRate = sampleRate,
            blockFrames = blockFrames,
            commLink = commLink,
            ignitorRegistry = IgnitorRegistry().apply { registerDefaults() },
            phasePoolSeed = phasePoolSeed,
            katalystRegistry = KatalystRegistry(),
            clock = clock,
            performanceTimeMs = performanceTimeMs,
            voiceWatch = voiceWatch,
        )
    }
}
